import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import SideNav from '../../components/Admin/SideNav';
import { Grid, makeStyles, Button } from '@material-ui/core';
import Axios from '../../axios-default';
import { loadSystemAnnouncement } from '../../store/SystemAnnouncement/systemAnnouncement.action';
import moment from 'moment';
import { useLang } from '../../util/lang';
import { useHistory } from 'react-router-dom';
import { easySnackbar } from '../../store/Noti/noti.actions';

const announcementStyles = makeStyles((theme) => ({
    '@global': {
        body: {
            background: '#fdfdfd',
        },
    },
    rootContainer: {
        padding: 16,
        margin: '38px 0px 0px 250px',
        [theme.breakpoints.between('xs', 'sm')]: {
            margin: '12px 0px 0px 0px',
            width: '100%',
        },
        width: 'calc(100% - 340px)',
    },
    borderContainer: {
        padding: 16,
        margin: '38px 0px 0px 250px',
        [theme.breakpoints.between('xs', 'sm')]: {
            margin: '12px 0px 0px 0px',
            width: '100%',
        },
        width: 'calc(100% - 340px)',
        border: '1px solid black',
    },
    linkBtn: {
        background: 'rgba(89,54,135,1)',
        color: 'white',
        padding: '4px 24px',
        fontWeight: 300,
        margin: '16px 0px 0px',
        width: '100%',
        [theme.breakpoints.up('sm')]: {
            padding: '4px 72px',
            width: 'unset',
        },
        '&:hover': {
            background: 'rgb(77, 74, 142)',
            color: 'white',
        },
    },
    inputField: {
        display: 'flex',
        alignItems: 'center',
    },
    largeTextField: {
        width: '100%',
        height: '100%',
    },
    buttonField: {
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
    },
}));

const SystemAnnouncement = () => {
    const classes = announcementStyles();
    const dispatch = useDispatch();
    const history = useHistory();
    const announcementGetLang = useLang('SystemAnnouncement');
    const systemAnnouncement = useSelector((state) => state.systemAnnouncement);
    const [formData, setFormData] = useState({
        systemName: '',
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        information: '',
    });
    const handleFormUpdate = useCallback(
        (field) =>
            ({ target: { value } }) => {
                setFormData({
                    ...formData,
                    [field]: value,
                });
            },
        [formData]
    );

    const handleSave = async () => {
        try {
            const { systemName, startDate, startTime, endDate, endTime, information } = formData;
            const updateObj = {
                systemName,
                information,
                startDateTime:
                    startDate && startTime
                        ? moment(startDate + ' ' + startTime)
                              .utc()
                              .format('YYYY-MM-DD HH:mm:ss')
                        : null,
                endDateTime:
                    endDate && endTime
                        ? moment(endDate + ' ' + endTime)
                              .utc()
                              .format('YYYY-MM-DD HH:mm:ss')
                        : null,
            };

            if (
                moment(updateObj.startDateTime).isAfter(updateObj.endDateTime) ||
                (!updateObj.startDateTime && !!updateObj.endDateTime) ||
                (!!updateObj.startDateTime && !updateObj.endDateTime)
            )
                throw 'Invalid Date Range.';

            const putRes = await Axios.put('/systemannouncement', updateObj);

            if (!putRes.data?.success) throw 'System Announcement Save Failure: Network Error.';

            dispatch(loadSystemAnnouncement());

            dispatch(easySnackbar('System Announcement saved successfully.', 'success'));
        } catch (error) {
            dispatch(easySnackbar(error, 'Error'));
        }
    };

    useEffect(() => {
        if (!localStorage.role.includes('SADMIN')) history.push('/admin/dashboard');
        dispatch(loadSystemAnnouncement());
    }, []);

    useEffect(() => {
        const { startDateTime, endDateTime } = systemAnnouncement;
        setFormData({
            systemName: systemAnnouncement.systemName,
            startDate: startDateTime ? moment(startDateTime).format('YYYY-MM-DD') : '',
            startTime: startDateTime ? moment(startDateTime).format('HH:mm') : '',
            endDate: endDateTime ? moment(endDateTime).format('YYYY-MM-DD') : '',
            endTime: endDateTime ? moment(endDateTime).format('HH:mm') : '',
            information: systemAnnouncement.information,
        });
    }, [systemAnnouncement]);

    return (
        <>
            <SideNav />
            <Grid container className={classes.rootContainer}></Grid>
            <Grid container className={classes.borderContainer} style={{ marginTop: 0 }}>
                <Grid item sm={12}>
                    <h2>{announcementGetLang('systemAnnouncement')}</h2>
                </Grid>
                <Grid item sm={5}>
                    <h4>{announcementGetLang('systemName')} :</h4>
                </Grid>
                <Grid item sm={3} className={classes.inputField}>
                    <input type={'text'} onChange={handleFormUpdate('systemName')} value={formData.systemName}></input>
                </Grid>
                <Grid item sm={4}></Grid>
                <Grid item sm={5}>
                    <h4>{announcementGetLang('effectiveFromDateTime')} :</h4>
                </Grid>
                <Grid item sm={2} className={classes.inputField}>
                    <input type={'date'} onChange={handleFormUpdate('startDate')} value={formData.startDate}></input>
                </Grid>
                <Grid item sm={2} className={classes.inputField}>
                    <input type={'time'} onChange={handleFormUpdate('startTime')} value={formData.startTime}></input>
                </Grid>
                <Grid item sm={3}></Grid>
                <Grid item sm={5}>
                    <h4>{announcementGetLang('effectiveToDateTime')} :</h4>
                </Grid>
                <Grid item sm={2} className={classes.inputField}>
                    <input type={'date'} onChange={handleFormUpdate('endDate')} value={formData.endDate}></input>
                </Grid>
                <Grid item sm={2} className={classes.inputField}>
                    <input type={'time'} onChange={handleFormUpdate('endTime')} value={formData.endTime}></input>
                </Grid>
                <Grid item sm={3}></Grid>
                <Grid item sm={5}>
                    <h4>{announcementGetLang('information')} :</h4>
                </Grid>

                <Grid item sm={4} className={classes.inputField}>
                    <textarea
                        rows="6"
                        cols="70"
                        onChange={handleFormUpdate('information')}
                        value={formData.information}
                    ></textarea>
                </Grid>
                <Grid item sm={3} className={classes.buttonField}>
                    <Button className={classes.linkBtn} onClick={handleSave}>
                        {announcementGetLang('Save')}
                    </Button>
                </Grid>
            </Grid>
        </>
    );
};

export default SystemAnnouncement;
